/* eslint-disable no-underscore-dangle */
/**
 * The two halves of the pair `AssignSheet` takes — one sheet, two portals.
 *
 *   operatorAssignSource    an operator putting its OWN driver and car on
 *                           its OWN trip. There is no operator to choose (it
 *                           is always itself) and no money at all.
 *   dispatcherAssignSource  the office moving a trip between operators, or
 *                           ADDING a slot it has not got yet: it chooses the
 *                           operator too, and sets THE OPERATOR FEE — the
 *                           one money field a dispatcher may touch.
 *
 * A source is `{ office, load(trip, { add }), save(input), copy }` and is the
 * only thing that differs between the two dialogs, so the sheet itself knows
 * nothing about either endpoint's shape. Every choice is reduced here to
 * `{ id, label, operatorId? }`, and `operatorId` is what lets the sheet show
 * only the chosen operator's people — the pairing the API will accept.
 *
 * ADDING is the office's alone: an operator does not decide that a trip
 * needs a guide. The operator source ignores the flag, which is the same
 * thing as refusing it — its endpoint has no such request to make.
 */
import {
  assignOperatorTrip, operatorDriverService, operatorVehicleService,
} from '../../Services/OperatorPortalApi';
import {
  assignDispatcherTrip, fetchDispatcherAssignOptions,
} from '../../Services/DispatcherPortalApi';
import { vehicleName } from './portalFormat';

const asList = (rows) => (Array.isArray(rows) ? rows : []);
const isActive = (row) => Number(row?.active) === 1;

export const operatorAssignSource = {
  office: false,
  load: async () => {
    const [drivers, vehicles] = await Promise.all([
      operatorDriverService.list(),
      operatorVehicleService.list(),
    ]);

    return {
      drivers: asList(drivers).filter(isActive).map((driver) => ({
        id: driver.Id, label: driver.full_name,
      })),
      vehicles: asList(vehicles).filter(isActive).map((car) => ({
        id: car.Id, label: vehicleName(car) || 'Unnamed vehicle',
      })),
      // Nothing to fill in from the server: the trip's own driver and
      // vehicle are already on screen, and an empty one has always meant
      // "keep what is there".
      slot: {},
    };
  },
  save: ({ trip, driverId, carId }) => assignOperatorTrip({
    bookingId: trip._id, driverId, carId, role: trip.role,
  }),
  copy: {
    title: (trip) => (trip?.driver ? 'Change driver & vehicle' : 'Assign driver'),
    // Never reached: the operator screens do not offer an addition.
    addTitle: 'Assign driver',
    loading: 'Loading your drivers and vehicles…',
    hint: 'The customer is sent the driver\'s name and the plate by email and WhatsApp.',
    notFound: 'This trip, driver or vehicle is no longer yours. Refresh and try again.',
    empty: 'You have no active drivers or vehicles yet. Add them under Drivers & vehicles.',
  },
};

export const dispatcherAssignSource = {
  office: true,
  load: async (trip, { add = false } = {}) => {
    const payload = await fetchDispatcherAssignOptions(trip, { add });

    return {
      operators: asList(payload?.operators).map((operator) => ({
        id: operator.id, label: operator.name || 'Unnamed operator',
      })),
      drivers: asList(payload?.drivers).map((driver) => ({
        id: driver.id, operatorId: driver.operator_id, label: driver.full_name || 'Unnamed driver',
      })),
      vehicles: asList(payload?.vehicles).map((vehicle) => ({
        id: vehicle.id, operatorId: vehicle.operator_id, label: vehicleName(vehicle) || 'Unnamed vehicle',
      })),
      // The slot as the SERVER has it — including the fee the dispatcher is
      // about to replace, and the car id a board row deliberately never
      // carries (a row names the plate, not an id).
      slot: {
        operatorId: payload?.slot?.operator_id || '',
        driverId: payload?.slot?.driver_id || '',
        carId: payload?.slot?.car_id || '',
        fee: {
          amount: payload?.slot?.operator_price,
          currency: payload?.slot?.operator_currency || 'EUR',
        },
      },
    };
  },
  save: async (input) => (await assignDispatcherTrip(input))?.trip,
  copy: {
    title: (trip) => (trip?.operator ? 'Change operator, driver & vehicle' : 'Assign an operator'),
    addTitle: 'Add someone to this trip',
    loading: 'Loading operators, drivers and vehicles…',
    hint: 'The customer is sent the driver\'s name and the plate by email and WhatsApp.',
    notFound: 'This trip or its operator has changed. Refresh the board and try again.',
    empty: 'There are no active operators, drivers or vehicles to choose from.',
  },
};
